import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Loader, Currency, Field } from "@/components/Atoms";
import { UserSearchInput } from "@/components/admin/UserSearchInput";
import {
  Shield, Users, Crown, Coins, Settings, Wrench, Ban, ScrollText, Map as MapIcon,
  UserCog, Layers, Gift, PackageOpen, Server, Lock, KeyRound, Flame, Calendar,
  Flag, Sparkles, RefreshCw
} from "lucide-react";



export function WelcomeReset() {
  const toast = useToast();
  const [uid, setUid] = useState("");
  const [server, setServer] = useState("");
  const [servers, setServers] = useState([]);
  useEffect(() => {
    api.get("/live/servers").then(({ data }) => {
      const list = data.servers || [];
      setServers(list);
      if (list.length > 0) setServer(list[0].name || list[0].slug);
    }).catch(() => {});
  }, []);
  const resetOne = async () => {
    if (!uid || !server) return toast.error("Player UID + server required");
    try { const { data } = await api.post("/admin/welcome/reset", { player_uid: uid, server_name: server }); toast.success(`Reset (${data.rows_deleted} row)`); setUid(""); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resetAll = async () => {
    if (!server) return;
    if (!window.confirm(`Reset welcome packages for EVERY player on ${server}? Mass operation.`)) return;
    try { const { data } = await api.post(`/admin/welcome/reset-all?server_name=${encodeURIComponent(server)}`); toast.success(`Reset ${data.rows_deleted} rows`); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4 fade-up" data-testid="welcome-tab">
      <div className="glass-card p-4">
        <h3 className="text-lg mb-1" style={{ fontFamily: "'Cinzel Decorative', serif" }}>Welcome Package Reset</h3>
        <p className="text-xs text-[var(--muted)]">
          Removes a player's <code>welcomed_players</code> row so they'll receive the welcome kit again the next time they log in to the server.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="glass-card p-5 space-y-3" data-testid="welcome-reset-one">
          <h3 className="label">Reset for One Player</h3>
          <Field label="Player (search by Discord/in-game name)">
            <UserSearchInput
              value={uid}
              onChange={setUid}
              onPick={(row) => {
                setUid(row.player_userid || "");
                if (row.server_name) setServer(row.server_name);
              }}
              scope="player_userid"
              serverName={server}
              placeholder="Start typing — fills the Player UID"
              dataTestid="welcome-uid"
            />
          </Field>
          <Field label="Server">
            <select className="input" value={server} onChange={(e) => setServer(e.target.value)} data-testid="welcome-server">
              {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
            </select>
          </Field>
          <button onClick={resetOne} className="btn" data-testid="welcome-reset-submit">Reset</button>
        </div>
        <div className="glass-card p-5 space-y-3" data-testid="welcome-reset-all">
          <h3 className="label">Reset for ALL Players on a Server</h3>
          <p className="text-xs text-[var(--muted)]">Useful before re-issuing a welcome kit campaign or after major item changes.</p>
          <Field label="Server">
            <select className="input" value={server} onChange={(e) => setServer(e.target.value)}>
              {servers.map((s) => <option key={s.slug} value={s.name || s.slug}>{s.name || s.slug}</option>)}
            </select>
          </Field>
          <button onClick={resetAll} className="btn btn-danger" data-testid="welcome-reset-all-submit">Reset ALL on {server}</button>
        </div>
      </div>
    </div>
  );
}
